import { useState, useEffect, useCallback } from "react";
import { IProgram, ISpeakerExtended } from "../../interfaces/models/IEvent";
import { getPersonById } from "../../Services/Person";
import { getGuestSpeakerById } from "../../Services/GuestSpeaker";
import { getFirmById } from "../../Services/Firm";
import { getCompanyById } from "../../Services/Company";
import { resolvePersonDisplayTitle } from "../../utils/personDisplayTitle";
import { programsNeedEnrichment } from "./programEnrichment";
import { useOptionalEventDetailData } from "./EventDetailDataContext";

type SpeakerPersonDetails = Partial<
  Pick<
    ISpeakerExtended,
    | "name"
    | "photo"
    | "title"
    | "firmName"
    | "firmLogo"
    | "lastName"
    | "legacyId"
  >
>;

/**
 * Enriches program speakers that only have personId and speakerType
 * by fetching Person or GuestSpeaker details from the API.
 * Used as fallback when the event API doesn't return enriched speakers.
 * Person details are cached per personId, but speakerType is always
 * preserved from each session's own speaker entry.
 */
export function useEnrichedPrograms(programs: IProgram[] | undefined) {
  const eventDetailData = useOptionalEventDetailData();
  const [enrichedPrograms, setEnrichedPrograms] = useState<IProgram[]>([]);

  const enrichPrograms = useCallback(async (progs: IProgram[]) => {
    if (!progs?.length) {
      setEnrichedPrograms([]);
      return;
    }

    if (!programsNeedEnrichment(progs)) {
      setEnrichedPrograms(progs);
      eventDetailData?.setCachedEnrichedPrograms(progs, progs);
      return;
    }

    const cached = eventDetailData?.getCachedEnrichedPrograms(progs);
    if (cached) {
      setEnrichedPrograms(cached);
      return;
    }

    const personsToEnrich = new Map<string, { isGuest: boolean }>();

    const collectSpeakers = (speakers: ISpeakerExtended[] | undefined) => {
      speakers?.forEach((speaker) => {
        const needsPhotoOrLogo = !speaker.photo || !speaker.firmLogo;
        const needsTitleFix =
          !speaker.title || speaker.title.trim().toLowerCase() === "other";
        if (
          speaker.personId &&
          (!speaker.name || needsPhotoOrLogo || needsTitleFix)
        ) {
          const speakerType = speaker.speakerType || "speaker";
          const isGuest =
            speaker.personSource === "guestSpeaker" || speakerType === "guest";
          if (!personsToEnrich.has(speaker.personId)) {
            personsToEnrich.set(speaker.personId, {
              isGuest,
            });
          }
        }
      });
    };

    progs.forEach((program) => {
      collectSpeakers(program.speakers);
      collectSpeakers(program.postEventSpeakers);
    });

    if (personsToEnrich.size === 0) {
      setEnrichedPrograms(progs);
      eventDetailData?.setCachedEnrichedPrograms(progs, progs);
      return;
    }

    const enrichedMap = new Map<string, SpeakerPersonDetails>();

    await Promise.all(
      Array.from(personsToEnrich.entries()).map(
        async ([personId, { isGuest }]) => {
          try {
            if (isGuest) {
              const guestSpeaker = await getGuestSpeakerById(personId);
              if (guestSpeaker) {
                const fullName =
                  `${guestSpeaker.firstName || ""} ${guestSpeaker.lastName || ""}`.trim();
                let firmName: string | undefined;
                let firmLogo: string | undefined;
                if (guestSpeaker.company) {
                  const company = await getCompanyById(guestSpeaker.company);
                  firmName = company?.name;
                  firmLogo = company?.logo;
                }
                enrichedMap.set(personId, {
                  name: fullName,
                  photo: guestSpeaker.photo,
                  title: resolvePersonDisplayTitle(guestSpeaker),
                  firmName,
                  firmLogo,
                  lastName: guestSpeaker.lastName,
                });
              }
            } else {
              let person = null;
              try {
                person = await getPersonById(personId);
              } catch {
                person = null;
              }

              if (person) {
                const fullName =
                  `${person.name || ""} ${person.lastName || ""}`.trim();
                let firmName: string | undefined;
                let firmLogo: string | undefined;
                if (person.firm) {
                  const firm = await getFirmById(person.firm);
                  firmName = firm?.name || (firm as any)?.firmName;
                  firmLogo = (firm as any)?.logo;
                }
                enrichedMap.set(personId, {
                  name: fullName,
                  photo: person.photo,
                  title: resolvePersonDisplayTitle(person),
                  firmName,
                  firmLogo,
                  legacyId: person.legacyId,
                  lastName: person.lastName,
                });
              } else {
                const guestSpeaker = await getGuestSpeakerById(personId);
                if (guestSpeaker) {
                  const fullName =
                    `${guestSpeaker.firstName || ""} ${guestSpeaker.lastName || ""}`.trim();
                  let firmName: string | undefined;
                  let firmLogo: string | undefined;
                  if (guestSpeaker.company) {
                    const company = await getCompanyById(guestSpeaker.company);
                    firmName = company?.name;
                    firmLogo = company?.logo;
                  }
                  enrichedMap.set(personId, {
                    name: fullName,
                    photo: guestSpeaker.photo,
                    title: resolvePersonDisplayTitle(guestSpeaker),
                    firmName,
                    firmLogo,
                    lastName: guestSpeaker.lastName,
                  });
                }
              }
            }
          } catch (err) {
            console.warn(
              `[useEnrichedPrograms] Failed to enrich speaker ${personId}:`,
              err
            );
          }
        }
      )
    );

    const enrichSpeakerArray = (speakers: ISpeakerExtended[] | undefined) =>
      speakers?.map((s) => {
        const personDetails = enrichedMap.get(s.personId);
        if (!personDetails) {
          return s;
        }
        return { ...s, ...personDetails };
      }) ?? undefined;

    const result = progs.map((program) => {
      const enrichedSpeakers = enrichSpeakerArray(program.speakers);
      const enrichedPostEventSpeakers = enrichSpeakerArray(program.postEventSpeakers);
      const hasChanges =
        (enrichedSpeakers && enrichedSpeakers.length > 0) ||
        (enrichedPostEventSpeakers && enrichedPostEventSpeakers.length > 0);
      if (!hasChanges) {
        return program;
      }
      return {
        ...program,
        ...(enrichedSpeakers && { speakers: enrichedSpeakers }),
        ...(enrichedPostEventSpeakers && { postEventSpeakers: enrichedPostEventSpeakers }),
      };
    });

    setEnrichedPrograms(result);
    eventDetailData?.setCachedEnrichedPrograms(progs, result);
  }, [eventDetailData]);

  useEffect(() => {
    enrichPrograms(programs ?? []);
  }, [programs, enrichPrograms]);

  return enrichedPrograms;
}
